import { useTranslationContext } from 'contexts/TranslationContext/TranslationContext';
import { useState } from 'react';
import { useAppDispatch } from 'store/store';
import {
  useLoginMutation,
  useResetPasswordMutation,
  useLazyValidateEmailQuery,
} from 'services/authService';
import { isFetchBaseQueryError } from 'utils';
import { useNavigate } from 'features/cobrand/hooks/useCobrandUtils';
import {
  setCredentials,
  setHospitalLogoUrl,
  setIsCaregiverWaitingForApproval,
  setIsLoggedIn,
  setUser,
} from '../authSlice';
import { AuthFormInputs, LoginAccessRestrictionError } from '../types';

type OnErrorCallback = (error: string) => void;
export const useLogin = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { translation } = useTranslationContext();
  const [login, { isLoading: isLoginLoading }] = useLoginMutation();
  const [resetPassword, { isLoading: isResetPasswordLoading }] = useResetPasswordMutation();
  const [validateEmail] = useLazyValidateEmailQuery();
  const [showAccessRestrictionDialog, setShowAccessRestrictionDialog] = useState(false);

  const onSubmitForm = async (requestData: AuthFormInputs, onErrorCallback?: OnErrorCallback) => {
    try {
      const { data: loginResponse, error } = await login(requestData);

      if (loginResponse?.accessToken && loginResponse?.refreshToken) {
        const logoUrl = loginResponse.hospital?.client_logo_dark_url;
        dispatch(setHospitalLogoUrl(logoUrl));

        dispatch(
          setCredentials({
            accessToken: loginResponse.accessToken,
            refreshToken: loginResponse.refreshToken,
          }),
        );
        dispatch(setUser(loginResponse));
        dispatch(setIsLoggedIn(true));
      }
      if (error) {
        if (!isFetchBaseQueryError(error)) {
          // Handle SerializedError or any other error type
          // eslint-disable-next-line no-console
          console.error('Login error:', error);
          return;
        }

        if (error.status === 423) {
          // Will trigger navigation to the PendingApproval screen in GuestGuard
          dispatch(setIsCaregiverWaitingForApproval({ value: true, persist: false }));
          return;
        }

        const errorData = error?.data as LoginAccessRestrictionError;
        const backendErrorMessage = errorData?.error?.trim().toLowerCase();

        if (error.status === 401 && backendErrorMessage === 'invalid email or password') {
          onErrorCallback?.(translation('invalid_email_or_password'));
          return;
        }

        // One-off case: Backend returns error_code instead of error for specific cases.
        // Using type assertion here to keep this localized and avoid modifying shared API types.
        // TODO: Remove once backend standardizes error response format.
        onErrorCallback?.(errorData?.error_code || errorData?.error || 'An unknown error occurred');
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log('Unable to sign in due to unexpected error');
    }
  };

  const validateEmailAction = async (email: string) => {
    try {
      // for member web, we could have used encodeURIComponent(email) but
      // for email value = test+123@wellframe, it will encode '+' to '%20' and '@' to '%40' - which does not work
      // we only want to encode '+' to '%2B' to make it work, following iOS approach here of hard coding
      const encodedEmail = email.replace(/\+/g, '%2B');
      const response = await validateEmail({ email: encodedEmail });
      if (response.error) {
        throw response.error;
      }
      return response;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log('Unable to validate email due to an error');
      throw error;
    }
  };

  const resetPasswordAction = async (email: string) => {
    try {
      const encodedEmail = email.replace(/\+/g, '%2B');
      const response = await resetPassword({ email: encodedEmail });

      if (response.error) {
        throw response.error;
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log('Unable to reset password due to an error');
      throw error;
    }
  };

  return {
    onSubmitForm,
    isLoading: isLoginLoading || isResetPasswordLoading,
    resetPassword: resetPasswordAction,
    validateEmail: validateEmailAction,
    showAccessRestrictionDialog,
    toggleAccessRestrictionDialog: () => setShowAccessRestrictionDialog((prev) => !prev),
  };
};
